import axios from '@axios'
import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import lookupsService from '@/vue.lookups.service'
import sessionService from '@/vue.session.service'

Vue.use(Vuex)

// authStore
const authStore = new Vuex.Store({
  namespaced: true,
  strict: process.env.NODE_ENV !== 'production',
  plugins: [
    createPersistedState({
      storage: {
        getItem: key => { try { return sessionService.getValue(key) } catch { return null } },
        setItem: (key, value) => sessionService.setValue(key, value),
        removeItem: key => sessionService.remove(key),
      },
    }),
  ],
  state: {
    userdata: null,
    aid: -1,
  },
  getters: {
    isLoggedIn: state => (state.userdata != null && !!state.userdata),
    userdata: state => (state.userdata ? state.userdata : null),
    username: state => (state.userdata && state.userdata.username ? state.userdata.username : null),
    fullName: state => (state.userdata && state.userdata.fullName ? state.userdata.fullName : null),
    allowedAreas: state => (state.userdata && state.userdata.settings && state.userdata.settings.allowedAreas ? state.userdata.settings.allowedAreas : []),
    roles: state => (state.userdata && state.userdata.settings && state.userdata.settings.roles ? state.userdata.settings.roles : []),
    selectedArea: state => state.aid,
  },
  actions: {
    selectArea({ commit }, aid) { commit('setArea', aid) },
    login({ commit }, userdata) { commit('setUser', userdata) },
    logout({ commit }) { commit('clear') },
  },
  mutations: {
    setUser(state, userdata) {
      state.userdata = userdata
    },
    clear(state) {
      state.userdata = null
      state.lastActivity = null
      state.lastRefreshed = null
      state.aid = -1
    },
    setArea(state, aid) {
      state.aid = aid
    },
  },
})

// authService
const authService = {
  redirectToLogin(provider) {
    window.location.replace(`/.auth/login/${provider}`)
  },

  async login(username) {
    const result = await axios.get('/api/auth/login')
    const userdata = result.data
    if (userdata !== null && userdata.username === username) {
      authStore.dispatch('login', userdata)
      const areas = await this.getAllowedAreas()
      await Promise.all([
        lookupsService.refreshCached(areas.map(a => a.aid))])

      if (areas.length > 0) return
      throw new Error(`Unauthorized login (no-areas) [${username}]`)
    }
    throw new Error(`Unauthorized login [${username}]`)
  },
  logout(deep) {
    authStore.dispatch('logout')
    lookupsService.clear()
    if (deep) {
      sessionService.removeAll()
      localStorage.clear()
    }
    window.location.replace('/.auth/logout')
  },

  async getClientUsername() {
    const response = await axios.get('/.auth/me')
    const { clientPrincipal } = response.data
    return (clientPrincipal ? clientPrincipal.userDetails : null)
  },

  isLoggedIn: () => authStore.getters.isLoggedIn,
  isUserInRole: role => authStore.getters.roles.includes(role),
  getUsername: () => authStore.getters.username,
  getFullName: () => authStore.getters.fullName,
  async currentArea() {
    const allowedAreas = await this.getAllowedAreas()
    let { selectedArea } = authStore.getters
    let area = allowedAreas.find(a => a.aid === selectedArea)
    if (!area && allowedAreas.length > 0) {
      // eslint-disable-next-line prefer-destructuring
      area = allowedAreas[0]
      selectedArea = area.aid
    }
    return area
  },
  async getAllowedAreas() {
    const areas = await lookupsService.getAreas()
    const { allowedAreas } = authStore.getters
    if (areas && Array.isArray(areas) && allowedAreas) return areas.filter(a => allowedAreas.includes(a.aid))
    return []
  },
  async isMultiArea() {
    const areas = await lookupsService.getAreas()
    return areas && areas.length > 1
  },
  selectArea(aid) {
    authStore.dispatch('selectArea', aid)
  },
  async saveSettings(aid, settings) {
    await axios.post('/api/auth/settings', JSON.stringify(settings), { params: { aid } })
    lookupsService.refreshAreas()
  },

  // keepalive
  keepAlive() {
    if (this.isLoggedIn()) {
      const now = (new Date()).getTime()
      sessionService.setValue('auth@keepAlive', now)
      sessionService.setValue('auth@keepUpdated', now)
    }
  },
  async checkTimeoutSession() {
    const lastActivity = sessionService.getValue('auth@keepAlive')
    const lastRefreshed = sessionService.getValue('auth@keepUpdated')
    const now = (new Date()).getTime()

    if (lastActivity && this.isLoggedIn()) {
      const elapsed = (now - lastActivity) / 1000 // seconds
      if (elapsed > 60 * 60) { // logout after 60 mins
        this.logout(false)
      }
    }
    if (lastRefreshed && this.isLoggedIn()) {
      const elapsed = (now - lastRefreshed) / 1000 // seconds

      if (elapsed > 5 * 60) { // refresh API functions after 5 mins
        sessionService.setValue('auth@keepUpdated', now)
      }
    }
  },

}
export default authService
